import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Check,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    TextField,
    TSingleSelectValue,
} from '../../../patterns';
import {formatToYearAndMonthAndDayWithDateInput, getLocaleFromLanguage} from '../base/dateUtils';
import {StoreContext} from '../base/root-store';
import Datepicker from '../../../patterns/components/forms/datepicker/datepicker';
import {getTooltipButton} from '../../../patterns/components/button/button';

export const AdHocQuestionModal = observer((): JSX.Element => {
    const store = useContext(StoreContext).questionStore;
    const userStore = useContext(StoreContext).userStore;
    const initialAdHocQuestion = store.variables.adHocQuestionInEditing;
    const companyLanguages = userStore.payload!.companyLanguages;

    const employeesStructuredBusinessUnitIds = userStore?.payload?.businessUnits
        ? userStore.payload!.businessUnits.map((unit) => unit.id)
        : [userStore.payload?.businessUnitId];

    const filteredBusinessUnits = store.payload!.allCompanySingleBusinessUnits.filter((it) => employeesStructuredBusinessUnitIds.includes(it.id));

    const [textEnglish, setTextEnglish] = useState<string>(initialAdHocQuestion?.questionLanguages.textEnglish ? initialAdHocQuestion?.questionLanguages.textEnglish : '');
    const [textEstonian, setTextEstonian] = useState<string>(initialAdHocQuestion?.questionLanguages.textEstonian ? initialAdHocQuestion?.questionLanguages.textEstonian : '');
    const [textFinnish, setTextFinnish] = useState<string>(initialAdHocQuestion?.questionLanguages.textFinnish ? initialAdHocQuestion?.questionLanguages.textFinnish : '');
    const [textLatvian, setTextLatvian] = useState<string>(initialAdHocQuestion?.questionLanguages.textLatvian ? initialAdHocQuestion?.questionLanguages.textLatvian : '');
    const [textLithuanian, setTextLithuanian] = useState<string>(initialAdHocQuestion?.questionLanguages.textLithuanian ? initialAdHocQuestion?.questionLanguages.textLithuanian : '');
    const [textPolish, setTextPolish] = useState<string>(initialAdHocQuestion?.questionLanguages.textPolish ? initialAdHocQuestion?.questionLanguages.textPolish : '');
    const [textSwedish, setTextSwedish] = useState<string>(initialAdHocQuestion?.questionLanguages.textSwedish ? initialAdHocQuestion?.questionLanguages.textSwedish : '');
    const [textRussian, setTextRussian] = useState<string>(initialAdHocQuestion?.questionLanguages.textRussian ? initialAdHocQuestion?.questionLanguages.textRussian : '');
    const [textJapanese, setTextJapanese] = useState<string>(initialAdHocQuestion?.questionLanguages.textJapanese ? initialAdHocQuestion?.questionLanguages.textJapanese : '');
    const [textVietnamese, setTextVietnamese] = useState<string>(initialAdHocQuestion?.questionLanguages.textVietnamese ? initialAdHocQuestion?.questionLanguages.textVietnamese : '');
    const [sendDate, setSendDate] = useState<Date | undefined>(initialAdHocQuestion ? new Date(initialAdHocQuestion?.sendDate) : undefined);
    const [businessUnitId, setBusinessUnitId] = useState<TSingleSelectValue>(initialAdHocQuestion ? initialAdHocQuestion.businessUnitId : filteredBusinessUnits[0].id);
    const [employeeSegment, setEmployeeSegment] = useState<TSingleSelectValue>(initialAdHocQuestion ? initialAdHocQuestion.employeeSegment : store.payload!.employeeSegments[0]);
    const [employeeLocation, setEmployeeLocation] = useState<TSingleSelectValue>(initialAdHocQuestion ? initialAdHocQuestion.employeeLocation : 'ALL');
    const [isOnlyFreetextAnswer, setOnlyFreetextAnswer] = useState<boolean>(initialAdHocQuestion ? initialAdHocQuestion?.onlyFreetextAnswer : false);
    const [activeField, setActiveField] = useState<string | null>(null);

    const languageMap: Record<string, { state: string; setter: (value: string) => void; labelKey: string }> = {
        en: { state: textEnglish, setter: setTextEnglish, labelKey: 'questions.question-modal.text-english' },
        et: { state: textEstonian, setter: setTextEstonian, labelKey: 'questions.question-modal.text-estonian' },
        fi: { state: textFinnish, setter: setTextFinnish, labelKey: 'questions.question-modal.text-finnish' },
        lv: { state: textLatvian, setter: setTextLatvian, labelKey: 'questions.question-modal.text-latvian' },
        lt: { state: textLithuanian, setter: setTextLithuanian, labelKey: 'questions.question-modal.text-lithuanian' },
        pl: { state: textPolish, setter: setTextPolish, labelKey: 'questions.question-modal.text-polish' },
        sv: { state: textSwedish, setter: setTextSwedish, labelKey: 'questions.question-modal.text-swedish' },
        ru: { state: textRussian, setter: setTextRussian, labelKey: 'questions.question-modal.text-russian' },
        ja: { state: textJapanese, setter: setTextJapanese, labelKey: 'questions.question-modal.text-japanese' },
        vi: { state: textVietnamese, setter: setTextVietnamese, labelKey: 'questions.question-modal.text-vietnamese' },
    };

    const locationOptions = store.payload!.employeeLocations.length > 1 ? <Select
        label={store.label('questions.ad-hoc-question-modal.location')}
        defaultValue={employeeLocation}
        onChange={(value): void => setEmployeeLocation(value)}
        options={[
            {
                label: store.label('questions.ad-hoc.location-all'),
                value: 'ALL',
            },
            ...store.payload!.employeeLocations.map((location) => ({
                label: store.label(`common.location.${location}`),
                value: location,
            }),
            )]
        }
        search={{
            label: store.label('questions.ad-hoc-question-modal.location-search'),
            id: 'ad-hoc-question-modal-location-search',
            name: 'ad-hoc-question-modal-location-search',
        }}
        id='ad-hoc-question-modal-location'
        name='ad-hoc-question-modal-location'
        type={undefined}
    /> : undefined;

    return (
        <Modal size="large">
            <ModalHeader>
                <h4>{store.variables.openModal === 'edit-adhoc-question' ? store.label('questions.ad-hoc-question-modal.title.edit') : store.label('questions.ad-hoc-question-modal.title.add')}</h4>
            </ModalHeader>
            <ModalContent>
                <div className="text">
                    <Datepicker
                        field={{
                            label: store.label('questions.ad-hoc-question-modal.date'),
                            focusLabel: store.label('questions.ad-hoc-question-modal.date'),
                            id: 'add-ad-hoc-question-date',
                            name: 'add-ad-hoc-question-date',
                            description: store.variables.shouldShowValidation && store.checkSendDate(sendDate) !== undefined ? store.label(`common.validation.${store.checkSendDate(sendDate)}`) : undefined,
                            invalid: store.variables.shouldShowValidation && store.checkSendDate(sendDate) !== undefined,
                        }}
                        format={'dd.MM.yyyy'}
                        value={sendDate}
                        type={undefined}
                        onDateChange={(value: Date | undefined): void => setSendDate(value)}
                        locale={getLocaleFromLanguage(userStore.payload?.language)}
                    />
                    {companyLanguages.map((language) => (
                        <div key={language} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <TextField
                                label={store.label(languageMap[language].labelKey)}
                                value={languageMap[language].state}
                                onChange={(value) => {
                                    languageMap[language].setter(value);
                                    setActiveField(language);
                                }}
                                onFocus={() => setActiveField(language)}
                                id={`add-ad-hoq-question-${language}`}
                                name={`add-ad-hoq-question-${language}`}
                                description={
                                    store.variables.shouldShowValidation && store.checkText(languageMap[language].state, language)
                                        ? store.label(`common.validation.${store.checkText(languageMap[language].state, language)}`)
                                        : undefined
                                }
                                invalid={store.variables.shouldShowValidation && store.checkText(languageMap[language].state, language) !== undefined}
                            />
                            {activeField === language && getTooltipButton({
                                icon: 'translate',
                                tooltip: store.label('questions.question-modal.translate'),
                                onClick: async () => await store.translateTextForLanguages(language, languageMap[language].state, companyLanguages, languageMap),
                            })}
                        </div>
                    ))}
                    <Select
                        label={store.label('questions.ad-hoc-question-modal.business-unit')}
                        defaultValue={businessUnitId}
                        onChange={(value): void => setBusinessUnitId(value)}
                        options={[
                            ...(
                                filteredBusinessUnits.some((businessUnit) => businessUnit.id === store.payload!.rootBusinessUnitId)
                                    ? [{
                                        label: filteredBusinessUnits.find((businessUnit) => businessUnit.id === store.payload!.rootBusinessUnitId)!.businessUnitName,
                                        value: store.payload!.rootBusinessUnitId,
                                    }]
                                    : []
                            ),
                            ...filteredBusinessUnits
                                .filter((businessUnit) => businessUnit.id !== store.payload!.rootBusinessUnitId)
                                .map((businessUnit) => ({
                                    label: businessUnit.businessUnitName,
                                    value: businessUnit.id,
                                }))
                                .sort((a, b) => a.label.localeCompare(b.label)),
                        ]}
                        search={{
                            label: store.label('questions.ad-hoc-question-modal.business-unit.search'),
                            id: 'ad-hoc-question-modal-search',
                            name: 'ad-hoc-question-modal-search',
                        }}
                        id='ad-hoc-question-modal-business-unit'
                        name='ad-hoc-question-modal-business-unit'
                        type={undefined}
                    />
                    <Select
                        label={store.label('questions.ad-hoc-question-modal.employee-segment')}
                        defaultValue={employeeSegment}
                        onChange={(value): void => setEmployeeSegment(value)}
                        options={[
                            {
                                label: store.label('questions.ad-hoc.employee-segment.ALL'),
                                value: store.payload!.employeeSegments[0],
                            },
                            ...store.payload!.employeeSegments.map((segment) => ({
                                label: store.label(`questions.ad-hoc.employee-segment.${segment}`),
                                value: segment,
                            })).filter((segment) => segment.value !== 'ALL'),
                        ]}
                        id='ad-hoc-question-modal-employee-segment'
                        name='ad-hoc-question-modal-employee-segment'
                        type={undefined}
                    />
                    {locationOptions}
                    <Check
                        label={store.label('questions.ad-hoc-question-modal.free-text')}
                        defaultChecked={isOnlyFreetextAnswer}
                        onChange={(checked): void => setOnlyFreetextAnswer(checked)}
                        id='is-active'
                        name='is-active'
                        value='is-active'/>
                </div>
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button block={true} color="white"
                                text={store.label('questions.ad-hoc-question-modal.cancel')}/>
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <Button block={true} text={store.label('company.modal.save')}
                            onClick={(): void => store.saveAdHocQuestion({
                                questionLanguages: {
                                    textEnglish: textEnglish !== '' ? textEnglish : undefined,
                                    textEstonian: textEstonian !== '' ? textEstonian : undefined,
                                    textFinnish: textFinnish !== '' ? textFinnish : undefined,
                                    textLatvian: textLatvian !== '' ? textLatvian : undefined,
                                    textLithuanian: textLithuanian !== '' ? textLithuanian : undefined,
                                    textPolish: textPolish !== '' ? textPolish : undefined,
                                    textSwedish: textSwedish !== '' ? textSwedish : undefined,
                                    textRussian: textRussian !== '' ? textRussian : undefined,
                                    textJapanese: textJapanese !== '' ? textJapanese : undefined,
                                    textVietnamese: textVietnamese !== '' ? textVietnamese : undefined},
                                sendDate: sendDate ? formatToYearAndMonthAndDayWithDateInput(sendDate as Date) : undefined,
                                businessUnitId: businessUnitId || '',
                                employeeSegment: employeeSegment || '',
                                employeeLocation: employeeLocation || '',
                                onlyFreetextAnswer: isOnlyFreetextAnswer,
                            })}/>
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
